// freshVerifier.js — fresh-context + HELD-OUT acceptance verification (arch-fable5 A3, audit-hardened).
// @arch:pure-engine
//
// Fable 5 doctrine + 2024–2026 literature (ImpossibleBench): a verifier the agent can READ is a
// gameable optimization target, not ground truth, and same-context self-critique is unreliable.
// The invariant-preserving fix is a FRESH, INDEPENDENT, agent-INVISIBLE held-out executable verifier
// run ALONGSIDE the primary set — green requires BOTH to pass, so a build that games the agent-visible
// checks but diverges from the held-out one (e.g. broken syntax caught by `node --check`) goes RED.
// The held-out set is still ground-truth EXECUTABLE verification (a second oracle, not a confidence
// score), so it strengthens the LAW rather than weakening it.
//
// PURE engine (Tier 1): no react, no window, no IPC. The clean-context runner (`runOracleFresh`) and
// the held-out verifier map are INJECTED by the renderer — this module only merges + normalizes.
//
// THE LAW: the oracle/replay path is still the SOLE judge of DONE. When disabled it returns null so
// the caller falls back to the existing in-context path → byte-identical to today.

/**
 * Build a fresh-context verifier.
 * @param {object} cfg
 * @param {boolean} [cfg.enabled=false] - false → the returned fn always yields null (no-op fallback).
 * @param {(args:{verifiers:object, taskText:string, signal?:any}) => Promise<object>} [cfg.runOracleFresh]
 *   the injected oracle runner, bound to a CLEAN context (fresh AbortController, no builder convo).
 * @returns {(args:{verifiers:object, heldOutVerifiers?:object, taskText:string, signal?:any})
 *   => Promise<(object|null)>} resolves to { green, noVerifiers, verdicts, fresh:true, heldOut } or null.
 */
export function makeFreshVerifier({ enabled = false, runOracleFresh } = {}) {
  return async function freshVerify({ verifiers, heldOutVerifiers, taskText, signal } = {}) {
    if (!enabled || typeof runOracleFresh !== 'function') return null; // caller uses the in-context path
    // The held-out (agent-invisible) verifiers run ALONGSIDE the primary set — green only if BOTH pass.
    const heldOut = heldOutVerifiers && Object.keys(heldOutVerifiers).length > 0;
    const merged = { ...(verifiers || {}), ...(heldOutVerifiers || {}) };
    if (Object.keys(merged).length === 0) {
      return { green: true, noVerifiers: true, verdicts: [], fresh: true, heldOut: false, evidence: 'no verifier (honest no-op)' };
    }
    const o = await runOracleFresh({ verifiers: merged, taskText, signal });
    return {
      green: !!(o?.reachedGreen || o?.noVerifiers),
      noVerifiers: !!o?.noVerifiers,
      verdicts: o?.verdicts || [],
      fresh: true,
      heldOut: !!heldOut,
    };
  };
}

export default makeFreshVerifier;
